import Phaser from 'phaser';

const ASSET_DIR = 'assets/sprites';

type IconKind = 'bomb' | 'range' | 'speed' | 'remote' | 'kick' | 'pierce' | 'shield' | 'life' | 'score';

export class BootScene extends Phaser.Scene {
  constructor() { super({ key: 'BootScene' }); }

  preload() {
    // Loading bar
    const w = this.cameras.main.width;
    const h = this.cameras.main.height;
    const barBg = this.add.rectangle(w / 2, h / 2, 420, 28, 0x131120).setStrokeStyle(2, 0xffd166);
    const barFill = this.add.rectangle(w / 2 - 210, h / 2, 0, 24, 0x862d91).setOrigin(0, 0.5);
    const label = this.add.text(w / 2, h / 2 - 40, 'BOMBASTIK', {
      fontFamily: 'monospace', fontSize: '32px', color: '#ffd166',
    }).setOrigin(0.5);
    const tip = this.add.text(w / 2, h / 2 + 40, 'se încarcă...', {
      fontFamily: 'monospace', fontSize: '14px', color: '#888',
    }).setOrigin(0.5);
    this.load.on('progress', (p: number) => barFill.width = 416 * p);
    this.load.on('complete', () => { barBg.destroy(); barFill.destroy(); label.destroy(); tip.destroy(); });

    // ---- Player sprites: 3 facings (down/up/side); right = flipped side ----
    this.load.image('player-geo-down', `${ASSET_DIR}/player-geo-down.png`);
    this.load.image('player-geo-up',   `${ASSET_DIR}/player-geo-up.png`);
    this.load.image('player-geo-side', `${ASSET_DIR}/player-geo-side.png`);
    this.load.image('player-aris-down', `${ASSET_DIR}/player-aris-down.png`);
    this.load.image('player-aris-up',   `${ASSET_DIR}/player-aris-up.png`);
    this.load.image('player-aris-side', `${ASSET_DIR}/player-aris-side.png`);

    // ---- Tiles ----
    this.load.image('tile-ground', `${ASSET_DIR}/tile-ground.png`);
    this.load.image('tile-hard',   `${ASSET_DIR}/tile-hard.png`);
    this.load.image('tile-soft',   `${ASSET_DIR}/tile-soft.png`);

    // ---- Bomb only ----
    this.load.image('bomb',              `${ASSET_DIR}/bomb.png`);
    this.load.image('bomb-remote',       `${ASSET_DIR}/bomb.png`);
    // Explosion textures are drawn programmatically in create() so the visible
    // flame is perfectly centered on a tile (the AI sprites were asymmetric).

    // ---- Enemies ----
    this.load.image('enemy-wanderer',     `${ASSET_DIR}/enemy-wanderer.png`);
    this.load.image('enemy-tracker',      `${ASSET_DIR}/enemy-tracker.png`);
    this.load.image('enemy-smarttracker', `${ASSET_DIR}/enemy-smarttracker.png`);
    this.load.image('enemy-dodger',       `${ASSET_DIR}/enemy-dodger.png`);

    // ---- Bosses (1..10) + princess ----
    for (let i = 1; i <= 10; i++) {
      this.load.image(`boss-w${i}`, `${ASSET_DIR}/boss-w${i}.png`);
    }
    this.load.image('princess', `${ASSET_DIR}/princess.png`);

    // Power-ups are drawn programmatically in create() for guaranteed
    // contrast and consistent visual style — see makePowerUpCard.
  }

  create() {
    this.makeAllPowerUpCards();
    this.makeExplosionTextures();
    this.scene.start('MenuScene');
  }

  // ---------- Programmatic flame textures (centered on tile) ----------

  private makeExplosionTextures() {
    // Center: a bright square fireball with concentric rings
    this.makeFlameCenter('explosion-center', 64);
    // Arm: a wider-than-tall jet so consecutive tiles overlap visually
    this.makeFlameArm('explosion-arm', 96, 56);
  }

  private makeFlameCenter(key: string, size: number) {
    const rt = this.add.renderTexture(0, 0, size, size).setVisible(false);
    const g = this.add.graphics();
    const cx = size / 2;
    const cy = size / 2;
    // Outer warm glow
    g.fillStyle(0xff6b35, 0.55);
    g.fillCircle(cx, cy, size * 0.48);
    // Mid hot ring
    g.fillStyle(0xff9f1c, 0.85);
    g.fillCircle(cx, cy, size * 0.36);
    // Bright yellow inner
    g.fillStyle(0xffd166, 1);
    g.fillCircle(cx, cy, size * 0.22);
    // White core
    g.fillStyle(0xffffff, 1);
    g.fillCircle(cx, cy, size * 0.10);
    // Spark tongues to make it pixel-flamey, four diagonals
    g.fillStyle(0xff6b35, 0.9);
    const spark = (a: number) => {
      const r1 = size * 0.30;
      const r2 = size * 0.50;
      const sx = cx + Math.cos(a) * r1;
      const sy = cy + Math.sin(a) * r1;
      const ex = cx + Math.cos(a) * r2;
      const ey = cy + Math.sin(a) * r2;
      const px = -Math.sin(a) * 6;
      const py =  Math.cos(a) * 6;
      g.fillTriangle(sx + px, sy + py, sx - px, sy - py, ex, ey);
    };
    for (let i = 0; i < 8; i++) spark((i / 8) * Math.PI * 2);
    rt.draw(g, 0, 0);
    g.destroy();
    rt.saveTexture(key);
    rt.destroy();
  }

  private makeFlameArm(key: string, w: number, h: number) {
    const rt = this.add.renderTexture(0, 0, w, h).setVisible(false);
    const g = this.add.graphics();
    // Long horizontal flame blob: outer glow, mid hot band, bright core line
    // Outer (warm tongue)
    g.fillStyle(0xff6b35, 0.55);
    g.fillEllipse(w / 2, h / 2, w, h * 0.95);
    // Mid hot
    g.fillStyle(0xff9f1c, 0.9);
    g.fillEllipse(w / 2, h / 2, w * 0.92, h * 0.65);
    // Bright yellow core
    g.fillStyle(0xffd166, 1);
    g.fillEllipse(w / 2, h / 2, w * 0.85, h * 0.40);
    // White spine
    g.fillStyle(0xffffff, 0.9);
    g.fillEllipse(w / 2, h / 2, w * 0.72, h * 0.18);
    rt.draw(g, 0, 0);
    g.destroy();
    rt.saveTexture(key);
    rt.destroy();
  }

  // ---------- Programmatic power-up cards ----------

  private makeAllPowerUpCards() {
    type Card = { key: string; fill: number; icon: IconKind };
    const cards: Card[] = [
      { key: 'pu-bomb',   fill: 0xef476f, icon: 'bomb' },
      { key: 'pu-range',  fill: 0xff9f1c, icon: 'range' },
      { key: 'pu-speed',  fill: 0x06d6a0, icon: 'speed' },
      { key: 'pu-remote', fill: 0x118ab2, icon: 'remote' },
      { key: 'pu-kick',   fill: 0xb5179e, icon: 'kick' },
      { key: 'pu-pierce', fill: 0xffd166, icon: 'pierce' },
      { key: 'pu-shield', fill: 0xc0c0c0, icon: 'shield' },
      { key: 'pu-life',   fill: 0xe63946, icon: 'life' },
      { key: 'pu-score',  fill: 0xfcbf49, icon: 'score' },
    ];
    for (const c of cards) this.makePowerUpCard(c.key, c.fill, c.icon);
  }

  /**
   * Cards rendered via Graphics directly into a Render Texture. Drawing the
   * icon as pure shape geometry (not text) guarantees the symbol always shows
   * up regardless of font availability or RT text quirks.
   */
  private makePowerUpCard(key: string, fill: number, icon: IconKind) {
    const size = 64;
    const cx = size / 2;
    const cy = size / 2;

    const rt = this.add.renderTexture(0, 0, size, size).setVisible(false);
    const g = this.add.graphics();
    // Outer white border for contrast against any tile
    g.fillStyle(0xffffff, 1).fillRoundedRect(0, 0, size, size, 10);
    // Dark inner backdrop frames the colored panel
    g.fillStyle(0x131120, 1).fillRoundedRect(3, 3, size - 6, size - 6, 8);
    // Colored panel
    g.fillStyle(fill, 1).fillRoundedRect(6, 6, size - 12, size - 12, 6);
    // Top highlight (lighter half-disc)
    g.fillStyle(0xffffff, 0.18).fillRoundedRect(6, 6, size - 12, (size - 12) / 2, 6);

    // ----- Icon shape -----
    this.drawIcon(g, icon, cx, cy);

    rt.draw(g, 0, 0);
    g.destroy();

    rt.saveTexture(key);
    rt.destroy();
  }

  private drawIcon(g: Phaser.GameObjects.Graphics, icon: IconKind, cx: number, cy: number) {
    const WHITE = 0xffffff;
    const BLACK = 0x131120;
    // Stroke ring helper for legibility on bright panels
    const stroke = (fn: () => void) => {
      g.lineStyle(2.5, BLACK, 1); fn();
    };

    switch (icon) {
      case 'bomb': {
        // Round black bomb + lit fuse
        g.fillStyle(BLACK, 1); g.fillCircle(cx, cy + 2, 13);
        g.fillStyle(WHITE, 0.9); g.fillCircle(cx - 4, cy - 2, 3);
        g.lineStyle(2, 0x6b3a16, 1); g.beginPath(); g.moveTo(cx + 8, cy - 9); g.lineTo(cx + 13, cy - 16); g.strokePath();
        g.fillStyle(0xffe066, 1); g.fillCircle(cx + 14, cy - 17, 3);
        g.fillStyle(0xff6b35, 1); g.fillCircle(cx + 14, cy - 19, 2);
        break;
      }
      case 'range': {
        // Horizontal double-headed arrow (range/reach)
        g.fillStyle(WHITE, 1);
        // Shaft
        g.fillRect(cx - 14, cy - 3, 28, 6);
        // Left arrow head
        g.fillTriangle(cx - 14, cy - 9, cx - 14, cy + 9, cx - 22, cy);
        // Right arrow head
        g.fillTriangle(cx + 14, cy - 9, cx + 14, cy + 9, cx + 22, cy);
        stroke(() => {
          g.strokeRect(cx - 14, cy - 3, 28, 6);
        });
        break;
      }
      case 'speed': {
        // Lightning-style triple chevron (>>)
        g.fillStyle(WHITE, 1);
        for (let i = 0; i < 3; i++) {
          const x = cx - 12 + i * 9;
          g.fillTriangle(x, cy - 10, x + 7, cy, x, cy + 10);
        }
        break;
      }
      case 'remote': {
        // Small remote control with antenna + red button
        g.fillStyle(BLACK, 1); g.fillRoundedRect(cx - 10, cy - 8, 20, 18, 3);
        g.fillStyle(0xef4444, 1); g.fillCircle(cx, cy + 1, 4);
        // Antenna line + tip
        g.lineStyle(2, WHITE, 1); g.beginPath(); g.moveTo(cx - 6, cy - 8); g.lineTo(cx - 12, cy - 16); g.strokePath();
        g.fillStyle(WHITE, 1); g.fillCircle(cx - 12, cy - 16, 2);
        break;
      }
      case 'kick': {
        // Boot silhouette
        g.fillStyle(BLACK, 1);
        // Leg shaft
        g.fillRect(cx - 5, cy - 12, 8, 14);
        // Foot
        g.fillRoundedRect(cx - 12, cy + 2, 22, 8, 2);
        // Sole highlight
        g.fillStyle(WHITE, 0.6); g.fillRect(cx - 12, cy + 8, 22, 2);
        break;
      }
      case 'pierce': {
        // Lightning bolt
        g.fillStyle(BLACK, 1);
        g.beginPath();
        g.moveTo(cx + 2, cy - 14);
        g.lineTo(cx - 8, cy + 2);
        g.lineTo(cx - 2, cy + 2);
        g.lineTo(cx - 4, cy + 14);
        g.lineTo(cx + 8, cy - 2);
        g.lineTo(cx + 2, cy - 2);
        g.closePath();
        g.fillPath();
        g.lineStyle(1.5, WHITE, 0.9); g.strokePath();
        break;
      }
      case 'shield': {
        // Classic shield outline with a centered cross
        g.fillStyle(BLACK, 1);
        g.beginPath();
        g.moveTo(cx, cy - 14);
        g.lineTo(cx + 12, cy - 8);
        g.lineTo(cx + 12, cy + 4);
        g.lineTo(cx, cy + 14);
        g.lineTo(cx - 12, cy + 4);
        g.lineTo(cx - 12, cy - 8);
        g.closePath();
        g.fillPath();
        g.fillStyle(WHITE, 1);
        g.fillRect(cx - 2, cy - 8, 4, 16);
        g.fillRect(cx - 8, cy - 2, 16, 4);
        break;
      }
      case 'life': {
        // Heart from two circles + triangle
        g.fillStyle(BLACK, 1);
        g.fillCircle(cx - 5, cy - 3, 6);
        g.fillCircle(cx + 5, cy - 3, 6);
        g.fillTriangle(cx - 10, cy - 1, cx + 10, cy - 1, cx, cy + 12);
        g.fillStyle(WHITE, 0.5); g.fillCircle(cx - 6, cy - 5, 2);
        break;
      }
      case 'score': {
        // Five-pointed star
        const points: Array<[number, number]> = [];
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? 12 : 5;
          const a = -Math.PI / 2 + (i * Math.PI) / 5;
          points.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
        }
        g.fillStyle(BLACK, 1);
        g.beginPath();
        g.moveTo(points[0][0], points[0][1]);
        for (let i = 1; i < points.length; i++) g.lineTo(points[i][0], points[i][1]);
        g.closePath();
        g.fillPath();
        break;
      }
    }
  }
}
